import { Alert, App, Button, Empty, Pagination, Spin, Switch, Tag } from "antd";
import { useMemo, useState, useSyncExternalStore } from "react";
import { useNavigate } from "react-router";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationDetail,
  useNotifications,
  useNotificationUnreadCount,
  useTrackNotificationClick,
  resolveNotificationActionUrl,
  type MemberNotification,
  type NotificationBusinessType,
  type NotificationCategory,
} from "@/entities/notification";

const categoryLabels: Record<NotificationCategory, string> = {
  PRODUCT_UPDATE: "产品动态",
  SYSTEM_NOTICE: "系统通知",
  ACCOUNT_NOTICE: "账户通知",
};

const businessTypeLabels: Record<NotificationBusinessType, string> = {
  NEW_MODEL_RELEASE: "模型上新",
  NEW_FEATURE_RELEASE: "新功能上线",
  PRICE_CHANGE: "价格变更",
  MAINTENANCE: "服务维护",
  ACCOUNT_NOTICE: "账号通知",
};

const categorySymbols: Record<NotificationCategory, string> = {
  PRODUCT_UPDATE: "✦",
  SYSTEM_NOTICE: "⌁",
  ACCOUNT_NOTICE: "◎",
};

const desktopMediaQuery = "(min-width: 861px)";

function subscribeDesktopLayout(callback: () => void) {
  const mediaQuery = window.matchMedia(desktopMediaQuery);
  mediaQuery.addEventListener("change", callback);
  return () => mediaQuery.removeEventListener("change", callback);
}

function getDesktopLayoutSnapshot() {
  return window.matchMedia(desktopMediaQuery).matches;
}

function formatMessageTime(value: string) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "时间未知";
  const difference = Date.now() - timestamp;
  const minutes = Math.floor(difference / 60_000);
  if (minutes < 1) return "刚刚";
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  return new Date(value).toLocaleDateString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
  });
}

function formatDetailTime(value: string) {
  return new Date(value).toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function NotificationIcon({
  notification,
}: {
  notification: MemberNotification;
}) {
  return <span>{categorySymbols[notification.category]}</span>;
}

function openNotificationUrl(
  url: string,
  navigate: ReturnType<typeof useNavigate>,
  onNavigate?: () => void,
) {
  const target = resolveNotificationActionUrl(url, window.location.origin);
  if (!target) return false;

  if (target.type === "external") {
    window.open(target.url, "_blank", "noopener,noreferrer");
    return true;
  }
  onNavigate?.();
  void navigate(target.url);
  return true;
}

interface MessageCenterWorkspaceProps {
  active?: boolean;
  compact?: boolean;
  onNavigate?: () => void;
}

export function MessageCenterWorkspace({
  active = true,
  compact = false,
  onNavigate,
}: MessageCenterWorkspaceProps) {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedId, setSelectedId] = useState<number>();
  const desktopLayout = useSyncExternalStore(
    subscribeDesktopLayout,
    getDesktopLayoutSnapshot,
    () => false,
  );
  const {
    data,
    error: listError,
    isLoading,
    refetch,
  } = useNotifications(
    {
      page,
      size: pageSize,
      unreadOnly,
    },
    active,
  );
  const { data: unreadCount } = useNotificationUnreadCount();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const trackClick = useTrackNotificationClick();
  const rows = useMemo(() => data?.items ?? [], [data?.items]);
  const selectedSummary =
    rows.find((item) => item.id === selectedId) ??
    (desktopLayout && !compact ? rows[0] : undefined);
  const {
    data: selectedDetail,
    error: detailError,
    isFetching: detailLoading,
    refetch: refetchDetail,
  } = useNotificationDetail(selectedSummary?.id, active);
  const selected = selectedDetail ?? selectedSummary;

  const openNotification = (notification: MemberNotification) => {
    setSelectedId(notification.id);
    if (notification.unread) {
      markRead.mutate(notification.id, {
        onError: (error) =>
          void message.error(
            error instanceof Error ? error.message : "标记已读失败",
          ),
      });
    }
  };

  const markCurrentRead = () => {
    if (!selected?.unread) return;
    markRead.mutate(selected.id, {
      onError: (error) =>
        void message.error(
          error instanceof Error ? error.message : "标记已读失败",
        ),
    });
  };

  const markAll = () => {
    markAllRead.mutate(undefined, {
      onSuccess: () => void message.success("全部消息已标记为已读"),
      onError: (error) =>
        void message.error(
          error instanceof Error ? error.message : "全部已读操作失败",
        ),
    });
  };

  const openAction = (notification: MemberNotification) => {
    const fallbackActionUrl = notification.actionUrl;
    if (!notification.actionText || !fallbackActionUrl) return;
    trackClick.mutate(notification.id, {
      onSuccess: (response) => {
        if (
          !openNotificationUrl(
            response.data?.actionUrl ?? fallbackActionUrl,
            navigate,
            onNavigate,
          )
        ) {
          void message.error("消息跳转地址无效");
        }
      },
      onError: (error) =>
        void message.error(error instanceof Error ? error.message : "跳转失败"),
    });
  };

  return (
    <section
      className={`message-center${compact ? " drawer-mode" : ""}${selected ? " has-selection" : ""}`}
    >
      <header className="message-center-header">
        <div>
          <span className="message-kicker">NOTIFICATIONS</span>
          <h2>消息中心</h2>
          <p>查看产品动态、系统通知和账户消息</p>
        </div>
        <div className="message-header-actions">
          <span>{unreadCount?.count ?? 0} 条未读</span>
          <Button
            disabled={!unreadCount?.count}
            loading={markAllRead.isPending}
            onClick={markAll}
          >
            全部已读
          </Button>
          <Button loading={isLoading} onClick={() => void refetch()}>
            刷新
          </Button>
        </div>
      </header>

      <div className="message-center-body">
        <aside className="message-list-panel">
          <div className="message-list-controls">
            <label className="unread-switch">
              <Switch
                size="small"
                checked={unreadOnly}
                onChange={(checked) => {
                  setUnreadOnly(checked);
                  setPage(1);
                  setSelectedId(undefined);
                }}
              />
              <span>只看未读</span>
            </label>
          </div>

          <div className="message-list" aria-live="polite">
            {isLoading ? (
              <div className="message-loading">
                <Spin />
              </div>
            ) : listError ? (
              <div className="message-empty">
                <Alert
                  type="error"
                  showIcon
                  message="消息加载失败"
                  description={listError.message}
                  action={
                    <Button size="small" onClick={() => void refetch()}>
                      重试
                    </Button>
                  }
                />
              </div>
            ) : rows.length ? (
              rows.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  className={`message-list-item${selected?.id === notification.id ? " active" : ""}${notification.unread ? " unread" : ""}`}
                  onClick={() => openNotification(notification)}
                >
                  <span
                    className={`message-avatar type-${notification.category.toLowerCase()}`}
                  >
                    <NotificationIcon notification={notification} />
                  </span>
                  <span className="message-list-copy">
                    <span className="message-list-title">
                      <b>{notification.title}</b>
                      {notification.newMessage && <Tag color="blue">NEW</Tag>}
                      {notification.priority !== "NORMAL" && (
                        <Tag
                          color={
                            notification.priority === "URGENT" ? "red" : "gold"
                          }
                        >
                          {notification.priority === "URGENT" ? "紧急" : "重要"}
                        </Tag>
                      )}
                    </span>
                    <span className="message-summary">
                      {notification.summary}
                    </span>
                    <span className="message-list-meta">
                      <em>{categoryLabels[notification.category]}</em>
                      <time>{formatMessageTime(notification.publishedAt)}</time>
                    </span>
                  </span>
                  {notification.coverUrl && (
                    <img
                      className="message-thumb"
                      src={notification.coverUrl}
                      alt=""
                      loading="lazy"
                    />
                  )}
                  {notification.unread && (
                    <i className="message-unread-dot" aria-label="未读" />
                  )}
                </button>
              ))
            ) : (
              <div className="message-empty">
                <Empty description={unreadOnly ? "没有未读消息" : "暂无消息"} />
              </div>
            )}
          </div>

          {(data?.total ?? 0) > pageSize && (
            <div className="message-pagination">
              <Pagination
                simple
                current={page}
                pageSize={pageSize}
                total={data?.total ?? 0}
                showSizeChanger
                onChange={(nextPage, nextSize) => {
                  setPage(nextSize === pageSize ? nextPage : 1);
                  setPageSize(nextSize);
                  setSelectedId(undefined);
                }}
              />
            </div>
          )}
        </aside>

        <article className="message-detail-panel">
          {!selected ? (
            <Empty description="选择一条消息查看详情" />
          ) : detailError ? (
            <div className="message-empty">
              <Alert
                type="error"
                showIcon
                message="消息详情加载失败"
                description={detailError.message}
                action={
                  <Button size="small" onClick={() => void refetchDetail()}>
                    重试
                  </Button>
                }
              />
            </div>
          ) : detailLoading && !selectedDetail ? (
            <div className="message-loading">
              <Spin />
            </div>
          ) : (
            <>
              <div className="message-detail-toolbar">
                <Button
                  type="text"
                  className="message-mobile-back"
                  onClick={() => setSelectedId(undefined)}
                >
                  ‹ 返回
                </Button>
                <span>{categoryLabels[selected.category]}</span>
                <div>
                  {selected.unread && (
                    <Button
                      type="link"
                      loading={markRead.isPending}
                      onClick={markCurrentRead}
                    >
                      标为已读
                    </Button>
                  )}
                </div>
              </div>
              {selected.coverUrl && (
                <img
                  className="message-detail-cover"
                  src={selected.coverUrl}
                  alt=""
                />
              )}
              <div className="message-detail-content">
                <div className="message-detail-labels">
                  <Tag>{businessTypeLabels[selected.businessType]}</Tag>
                  {selected.newMessage && <Tag color="blue">NEW</Tag>}
                  {selected.pinned && <Tag color="purple">置顶</Tag>}
                  {selected.priority !== "NORMAL" && (
                    <Tag
                      color={selected.priority === "URGENT" ? "red" : "gold"}
                    >
                      {selected.priority === "URGENT" ? "紧急消息" : "重要消息"}
                    </Tag>
                  )}
                  {selected.unread && <Tag color="blue">未读</Tag>}
                </div>
                <h3>{selected.title}</h3>
                <div className="message-sender">
                  <span className="message-avatar">
                    <NotificationIcon notification={selected} />
                  </span>
                  <span>
                    <b>Witemedia 团队</b>
                    <time>{formatDetailTime(selected.publishedAt)}</time>
                  </span>
                </div>
                <p className="message-detail-summary">{selected.summary}</p>
                <div className="message-rich-content">
                  {selected.body
                    .split("\n")
                    .map((paragraph, index) =>
                      paragraph ? (
                        <p key={`${selected.id}-${index}`}>{paragraph}</p>
                      ) : (
                        <br key={`${selected.id}-${index}`} />
                      ),
                    )}
                </div>
                {selected.actionText && selected.actionUrl && (
                  <Button
                    type="primary"
                    size="large"
                    loading={trackClick.isPending}
                    onClick={() => openAction(selected)}
                  >
                    {selected.actionText}
                  </Button>
                )}
              </div>
            </>
          )}
        </article>
      </div>
    </section>
  );
}
